import { YDatabase } from '@databases/collab/y-database';
import { collabStateManager } from '@databases/collab-state-manager';
import { getActivationId, getCloudId, getVersionId } from '@databases/database-state';
import { resetDatabaseLoadingState } from '@databases/database-state/state/initial-database-loading-state';
import { logs } from '@databases/logs/activity-logger';
import { type LogEntry } from '@databases/logs/types';
import { type DatabaseJSON } from '@databases/model/database';
import {
  buildDatabaseFromCollabAndContent,
  buildDefaultValueConverters,
  createCollabManager,
  syncCollabChangesToRedux,
} from '@databases/redux/collab/actions';
import { appStore } from '@databases/redux/store';
import { container } from '@databases/service-container';
import databases from '@databases/testing/databases';
import { type Database } from '@databases/types/database';
import { convertToYjs } from '@databases/utils/redux/collab/convert-to-collab';
import {
  addDatabaseToList,
  fakeDatabaseContent,
  markDatabaseAsLoaded,
} from '@databases/utils/redux/databases/actions';
import { snapshot } from 'valtio';

import { currentHostFrameContext } from '@canvas/canvas-state/host-api';
import {
  type ClientFeatureFlagName,
  isFeatureFlagEnabled,
  setFeatureFlagOverride,
} from '@canvas/client-feature-flags';
import {
  addOverrides,
  getOverrides,
} from '@canvas/client-feature-flags/overrides/persisted';
import { Action } from '@canvas/model/permissions';

export type DatabaseName = keyof typeof databases;

export type SetDatabaseOptions =
  | {
      databaseName?: DatabaseName;
    }
  | { contentId: string; json: DatabaseJSON };

const validUUID = '4d555e81-af99-4dfa-9073-db4e49df4f6e';

const setFlagOverride = (flag: string, value: boolean) => {
  addOverrides({ [flag]: value });
  setFeatureFlagOverride(getOverrides());

  const eventBus = container.resolve('EventBus');
  eventBus.dispatch('featureFlagsChanged', { [flag]: value });
};

const setPageExportStatus = (status: boolean) => {
  currentHostFrameContext.data.dataSecurityPolicy = {
    ...currentHostFrameContext.data.dataSecurityPolicy,
    pageExport: status,
  };
};

const getFlagValue = (flag: ClientFeatureFlagName) => {
  return isFeatureFlagEnabled(flag);
};

const setDatabase = async (options: SetDatabaseOptions) => {
  resetDatabaseLoadingState();

  let database;
  let contentId;

  let doc;

  if ('json' in options) {
    doc = YDatabase.fromJSON(options.json, {
      getCurrentUser: () => {
        throw new Error();
      },
    });
    contentId = options.contentId;
    database = buildDatabaseFromCollabAndContent(
      {
        id: contentId,
        referenceId: '1234',
        space: { id: 12345, key: 'ABC', name: 'ABC' },
        type: 'database',
        title: 'my database',
        ...fakeDatabaseContent,
      },
      doc,
      false
    );
  } else {
    let databaseName: keyof typeof databases = 'emptyDatabase';
    if ('databaseName' in options && options.databaseName) {
      databaseName = options.databaseName;
    }

    const selectedDatabase = databases[databaseName];
    database = selectedDatabase.database;
    contentId = database.contentId ?? selectedDatabase.contentId;
    doc = convertToYjs(database, buildDefaultValueConverters(), {});
  }

  appStore.dispatch(addDatabaseToList(database as Database));
  appStore.dispatch(markDatabaseAsLoaded(contentId));

  const cloudId = getCloudId();
  const activationId = getActivationId();
  const versionId = getVersionId();

  const collabManager = createCollabManager({
    contentId,
    spaceId: 1234,
    referenceId: validUUID,
    initialDatabase: doc,
    cloudId,
    activationId,
    versionId,
  });

  collabManager.database.doc.on(
    'update',
    syncCollabChangesToRedux(collabManager, appStore.dispatch, {
      contentId,
      isReadOnly: false,
    })
  );

  collabManager.setGrantsWithoutSync(new Set([Action.read, Action.write]));

  databaseState.setContentId(contentId);

  return database;
};

const addDatabase = (databaseName: DatabaseName) => {
  const selectedDatabase = databases[databaseName];
  appStore.dispatch(addDatabaseToList(selectedDatabase.database as Database));
  appStore.dispatch(markDatabaseAsLoaded(selectedDatabase.contentId));
};

export type DatabaseState = {
  reactAppIsReady: boolean;
  setContentId: (contentId: string) => void;
  setDatabase: (options: SetDatabaseOptions) => Promise<Database>;
  addDatabase: (databaseName: DatabaseName) => void;
  getLogs: () => readonly LogEntry[];
  setFlagOverride: (flag: string, value: boolean) => void;
  getFlagValue: (flag: ClientFeatureFlagName) => boolean;
  getDatabaseJSON: (contentId: string) => DatabaseJSON | undefined;
  setPageExportStatus: (status: boolean) => void;
};

export const databaseState: DatabaseState = {
  reactAppIsReady: false,
  setDatabase,
  addDatabase,
  setContentId: () => {},
  getLogs: () => snapshot(logs),
  setFlagOverride,
  getFlagValue,
  getDatabaseJSON: (contentId: string) =>
    collabStateManager.getCollabManager(contentId)?.database.toJSON(),
  setPageExportStatus,
};
